import { CSSProperties, useEffect, useRef } from "react";

// Fixed, slowly-drifting aurora blobs + a faded grid + a field of twinkling
// particles that give the whole page a sense of depth behind the content.
// The layers parallax gently with the cursor. Purely decorative.

interface Particle {
  left: string;
  top: string;
  size: number;
  dur: string;
  delay: string;
}

const PARTICLES: Particle[] = [
  { left: "8%", top: "18%", size: 3, dur: "9s", delay: "0s" },
  { left: "16%", top: "62%", size: 2, dur: "11s", delay: "1.5s" },
  { left: "24%", top: "34%", size: 4, dur: "8s", delay: "0.8s" },
  { left: "33%", top: "78%", size: 2, dur: "12s", delay: "2.2s" },
  { left: "42%", top: "12%", size: 3, dur: "10s", delay: "1s" },
  { left: "48%", top: "50%", size: 2, dur: "9.5s", delay: "3s" },
  { left: "57%", top: "26%", size: 3, dur: "8.5s", delay: "0.4s" },
  { left: "63%", top: "70%", size: 2, dur: "11.5s", delay: "1.8s" },
  { left: "71%", top: "40%", size: 4, dur: "9s", delay: "2.6s" },
  { left: "78%", top: "16%", size: 2, dur: "10.5s", delay: "0.6s" },
  { left: "84%", top: "58%", size: 3, dur: "8s", delay: "3.4s" },
  { left: "90%", top: "32%", size: 2, dur: "12s", delay: "1.2s" },
  { left: "94%", top: "74%", size: 3, dur: "9.5s", delay: "2s" },
  { left: "12%", top: "88%", size: 2, dur: "11s", delay: "0.2s" },
  { left: "52%", top: "86%", size: 3, dur: "10s", delay: "2.8s" },
  { left: "68%", top: "90%", size: 2, dur: "8.5s", delay: "1.4s" },
];

const AmbientBackground = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let frame = 0;

    const onMove = (e: MouseEvent) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const px = (e.clientX / window.innerWidth - 0.5) * 2; // -1..1
        const py = (e.clientY / window.innerHeight - 0.5) * 2; // -1..1
        el.style.setProperty("--px", px.toFixed(3));
        el.style.setProperty("--py", py.toFixed(3));
      });
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="fixed inset-0 -z-10 overflow-hidden pointer-events-none"
    >
      {/* Aurora blobs (parallax layer) */}
      <div className="aurora-parallax absolute inset-0">
        <div className="aurora-blob aurora-1 absolute -top-[15%] -left-[10%] w-[45vw] h-[45vw] bg-teal-400/20 dark:bg-teal-500/10" />
        <div className="aurora-blob aurora-2 absolute top-[20%] -right-[15%] w-[40vw] h-[40vw] bg-cyan-400/20 dark:bg-cyan-500/10" />
        <div className="aurora-blob aurora-3 absolute -bottom-[20%] left-[20%] w-[50vw] h-[50vw] bg-sky-400/15 dark:bg-sky-500/[0.08]" />
      </div>

      {/* Faded grid */}
      <div className="grid-overlay absolute inset-0" />

      {/* Twinkling particles (opposite parallax for depth) */}
      <div className="particle-parallax absolute inset-0">
        {PARTICLES.map((p, i) => (
          <span
            key={i}
            className="particle"
            style={
              {
                left: p.left,
                top: p.top,
                width: p.size,
                height: p.size,
                "--pdur": p.dur,
                "--pdelay": p.delay,
              } as CSSProperties
            }
          />
        ))}
      </div>
    </div>
  );
};

export default AmbientBackground;
